import { useState, useRef } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Upload, X, AlertCircle, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import AdminLayout from "./AdminLayout";

const DISTRICTS = [
  "中西區","灣仔區","東區","南區","油尖旺區","深水埗區","九龍城區",
  "黃大仙區","觀塘區","葵青區","荃灣區","屯門區","元朗區","北區",
  "大埔區","沙田區","西貢區","離島區"
];

interface School {
  id: number; name: string; district: string; schoolType: string;
  address?: string; phone?: string; email?: string; website?: string;
  contactPerson?: string; notes?: string;
}

interface JsonImportPreview {
  counts: { additions: number; updates: number; invalid: number; removals: number };
  additions: Array<{ name: string; district: string; schoolType: string }>;
  updates: Array<{ name: string; changes: Array<{ field: string; oldValue: string | null; newValue: string | null }> }>;
  invalid: Array<{ section: "added" | "changed"; index: number; reason: string }>;
  removals: Array<{ index: number; name: string }>;
}

const empty = (): Omit<School, "id"> => ({
  name: "", district: DISTRICTS[0], schoolType: "secondary",
  address: "", phone: "", email: "", website: "", contactPerson: "", notes: ""
});

async function adminFetch(path: string, init?: RequestInit, token?: string | null) {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

export default function AdminSchoolsPage() {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const af = async (path: string, init?: RequestInit) => adminFetch(path, init, await getToken());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<School | null>(null);
  const [form, setForm] = useState(empty());
  const [importResult, setImportResult] = useState<{ inserted: number; updated: number; skippedRemovals?: number; errors: string[] } | null>(null);
  const [importing, setImporting] = useState(false);
  const [jsonPreview, setJsonPreview] = useState<JsonImportPreview | null>(null);
  const [jsonImportFile, setJsonImportFile] = useState<File | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [sortCol, setSortCol] = useState<keyof School | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const fileRef = useRef<HTMLInputElement>(null);
  const jsonFileRef = useRef<HTMLInputElement>(null);

  const { data: schools = [], isLoading } = useQuery<School[]>({
    queryKey: ["admin-schools"],
    queryFn: () => af("/api/admin/schools"),
  });

  const save = useMutation({
    mutationFn: (data: Omit<School, "id"> & { id?: number }) => {
      const { id, ...body } = data;
      return af(id ? `/api/admin/schools/${id}` : "/api/admin/schools", {
        method: id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-schools"] }); setOpen(false); },
  });

  const remove = useMutation({
    mutationFn: (id: number) => af(`/api/admin/schools/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-schools"] }); setDeleteId(null); },
  });

  function openAdd() { setForm(empty()); setEditing(null); setOpen(true); }
  function openEdit(s: School) {
    setEditing(s);
    setForm({ ...s, address: s.address ?? "", phone: s.phone ?? "", email: s.email ?? "", website: s.website ?? "", contactPerson: s.contactPerson ?? "", notes: s.notes ?? "" });
    setOpen(true);
  }

  function downloadTemplate() {
    const header = "school_no,school_name,district,school_level,address,phone,email,website,contact_person,notes";
    const example = ",荃灣某中學,荃灣區,SECONDARY,荃灣某街1號,2345-6789,info@school.edu.hk,https://school.edu.hk,,";
    const blob = new Blob([header + "\n" + example], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "schools_template.csv";
    a.click();
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const result = await af("/api/admin/schools/import", { method: "POST", body: fd });
      setImportResult(result);
      qc.invalidateQueries({ queryKey: ["admin-schools"] });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleJsonImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setJsonPreview(null);
    setJsonImportFile(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const preview = await af("/api/admin/schools/import-json/preview", { method: "POST", body: fd });
      setJsonPreview(preview);
      setJsonImportFile(file);
    } finally {
      setImporting(false);
      if (jsonFileRef.current) jsonFileRef.current.value = "";
    }
  }

  async function confirmJsonImport() {
    if (!jsonImportFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", jsonImportFile);
      const result = await af("/api/admin/schools/import-json", { method: "POST", body: fd });
      setImportResult(result);
      setJsonPreview(null);
      setJsonImportFile(null);
      qc.invalidateQueries({ queryKey: ["admin-schools"] });
    } finally {
      setImporting(false);
    }
  }

  function closeJsonPreview() {
    if (importing) return;
    setJsonPreview(null);
    setJsonImportFile(null);
  }

  function handleSort(col: keyof School) {
    if (sortCol === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sortedSchools = [...schools].sort((a, b) => {
    if (!sortCol) return 0;
    const av = a[sortCol] ?? "";
    const bv = b[sortCol] ?? "";
    const cmp = typeof av === "number" && typeof bv === "number"
      ? av - bv
      : String(av).localeCompare(String(bv), "zh-HK");
    return sortDir === "asc" ? cmp : -cmp;
  });

  function SortIcon({ col }: { col: keyof School }) {
    if (sortCol !== col) return <ArrowUpDown className="inline ml-1 h-3 w-3 opacity-40" />;
    return sortDir === "asc"
      ? <ArrowUp className="inline ml-1 h-3 w-3" />
      : <ArrowDown className="inline ml-1 h-3 w-3" />;
  }

  const totalCount = schools.length;
  const secondaryCount = schools.filter(s => s.schoolType === "secondary").length;
  const primaryCount = schools.filter(s => s.schoolType === "primary").length;

  return (
    <AdminLayout tab="schools">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">學校管理</h2>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={downloadTemplate}>
            下載範本
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {importing ? "匯入中..." : "匯入 CSV"}
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <Button variant="outline" size="sm" onClick={() => jsonFileRef.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {importing ? "滙入中..." : "滙入JSON"}
          </Button>
          <input ref={jsonFileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleJsonImport} />
          <Button size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-1.5" /> 新增學校
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-4">
        <div className="flex items-center gap-1.5 rounded-lg border bg-card px-4 py-2 text-sm">
          <span className="text-muted-foreground">全部</span>
          <span className="font-bold text-base">{totalCount}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border bg-card px-4 py-2 text-sm">
          <span className="text-muted-foreground">中學</span>
          <span className="font-bold text-base">{secondaryCount}</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border bg-card px-4 py-2 text-sm">
          <span className="text-muted-foreground">小學</span>
          <span className="font-bold text-base">{primaryCount}</span>
        </div>
      </div>

      {importing && (
        <Card className="mb-4 border-blue-300 bg-blue-50">
          <CardContent className="p-4 text-sm flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600 shrink-0" />
            <div>
              <p className="font-medium text-blue-800">正在匯入學校資料，請稍候...</p>
              <p className="text-blue-600 text-xs mt-0.5">大批量資料需要較長時間，請勿關閉此頁面</p>
            </div>
          </CardContent>
        </Card>
      )}

      {importResult && !importing && (
        <Card className={`mb-4 ${importResult.errors.length > 0 ? "border-amber-300" : "border-green-300"}`}>
          <CardContent className="p-4 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              {importResult.inserted > 0 && <p>新增 {importResult.inserted} 筆</p>}
              {importResult.updated > 0 && <p>更新 {importResult.updated} 筆</p>}
              {(importResult.skippedRemovals ?? 0) > 0 && <p>待核實刪除 {importResult.skippedRemovals} 筆（未自動刪除）</p>}
              {importResult.inserted === 0 && importResult.updated === 0 && <p>無新資料</p>}
              {importResult.errors.length > 0 && <p className="text-amber-700 mt-1">錯誤：{importResult.errors.slice(0,3).join("；")}</p>}
            </div>
            <button className="ml-auto" onClick={() => setImportResult(null)}><X className="h-4 w-4" /></button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 cursor-pointer select-none" onClick={() => handleSort("id")}>ID<SortIcon col="id" /></TableHead>
                <TableHead className="w-72 cursor-pointer select-none" onClick={() => handleSort("name")}>學校名稱<SortIcon col="name" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort("district")}>地區<SortIcon col="district" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort("schoolType")}>類型<SortIcon col="schoolType" /></TableHead>
                <TableHead className="w-24">電話</TableHead>
                <TableHead className="w-56 cursor-pointer select-none" onClick={() => handleSort("website")}>網址<SortIcon col="website" /></TableHead>
                <TableHead className="w-24">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">載入中...</TableCell></TableRow>
              ) : schools.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">暫無學校資料</TableCell></TableRow>
              ) : sortedSchools.map(s => (
                <TableRow key={s.id}>
                  <TableCell className="text-muted-foreground text-xs">{s.id}</TableCell>
                  <TableCell className="font-medium w-72 max-w-[18rem] truncate" title={s.name}>{s.name}</TableCell>
                  <TableCell className="text-sm">{s.district}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{s.schoolType === "primary" ? "小學" : s.schoolType === "secondary" ? "中學" : s.schoolType}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{s.phone ?? "—"}</TableCell>
                  <TableCell className="text-sm">
                    {s.website ? (
                      <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[14rem] block" title={s.website}>
                        {s.website.replace(/^https?:\/\//, "")}
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => setDeleteId(s.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "編輯學校" : "新增學校"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium">學校名稱 *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="學校全名" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">地區 *</label>
              <Select value={form.district} onValueChange={v => setForm(f => ({ ...f, district: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">學校類型</label>
              <Select value={form.schoolType} onValueChange={v => setForm(f => ({ ...f, schoolType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">小學</SelectItem>
                  <SelectItem value="secondary">中學</SelectItem>
                  <SelectItem value="other">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium">地址</label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="學校地址" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">電話</label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="聯絡電話" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">電郵</label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="電郵地址" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">網站</label>
              <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">聯絡人</label>
              <Input value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} placeholder="負責人姓名" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium">備註</label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="備註" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>取消</Button>
            <Button onClick={() => save.mutate({ ...form, ...(editing ? { id: editing.id } : {}) })} disabled={save.isPending || !form.name}>
              {save.isPending ? "儲存中..." : "儲存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>確認刪除</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">此操作無法復原，確定要刪除這所學校嗎？</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>取消</Button>
            <Button variant="destructive" onClick={() => deleteId !== null && remove.mutate(deleteId)} disabled={remove.isPending}>
              {remove.isPending ? "刪除中..." : "確認刪除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={jsonPreview !== null} onOpenChange={open => { if (!open) closeJsonPreview(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>確認 JSON 學校匯入</DialogTitle>
          </DialogHeader>
          {jsonPreview && (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                以下只供預覽，尚未更改資料庫。確認後才會新增或更新學校。
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">新增</p><p className="text-xl font-bold">{jsonPreview.counts.additions}</p></div>
                <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">更新</p><p className="text-xl font-bold">{jsonPreview.counts.updates}</p></div>
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3"><p className="text-xs text-amber-700">無效／未配對</p><p className="text-xl font-bold text-amber-800">{jsonPreview.counts.invalid}</p></div>
                <div className="rounded-lg border border-slate-300 bg-slate-50 p-3"><p className="text-xs text-slate-600">待核實刪除</p><p className="text-xl font-bold">{jsonPreview.counts.removals}</p></div>
              </div>

              {jsonPreview.additions.length > 0 && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold">擬新增學校</h3>
                  <div className="max-h-48 overflow-y-auto rounded-md border">
                    {jsonPreview.additions.map((school, index) => (
                      <div key={`${school.name}-${index}`} className="flex items-center justify-between gap-4 border-b px-3 py-2 text-sm last:border-b-0">
                        <span className="font-medium">{school.name}</span>
                        <span className="text-muted-foreground">{school.district} · {school.schoolType === "primary" ? "小學" : school.schoolType === "secondary" ? "中學" : "其他"}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {jsonPreview.updates.length > 0 && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold">擬更新學校</h3>
                  <div className="max-h-64 space-y-2 overflow-y-auto">
                    {jsonPreview.updates.map((school, index) => (
                      <div key={`${school.name}-${index}`} className="rounded-md border p-3 text-sm">
                        <p className="font-medium">{school.name}</p>
                        {school.changes.length === 0 ? (
                          <p className="mt-1 text-xs text-muted-foreground">沒有可見欄位變更（可能只更新學校編號備註）</p>
                        ) : (
                          <div className="mt-2 space-y-1">
                            {school.changes.map((change, changeIndex) => (
                              <div key={`${change.field}-${changeIndex}`} className="grid grid-cols-[8rem_1fr] gap-2 text-xs">
                                <span className="text-muted-foreground">{change.field}</span>
                                <span><span className="line-through text-muted-foreground">{change.oldValue ?? "空白"}</span> → {change.newValue ?? "空白"}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {jsonPreview.invalid.length > 0 && (
                <section className="rounded-md border border-amber-300 bg-amber-50 p-3">
                  <h3 className="text-sm font-semibold text-amber-900">無效或未配對記錄（不會套用）</h3>
                  <ul className="mt-2 space-y-1 text-sm text-amber-800">
                    {jsonPreview.invalid.map(item => <li key={`${item.section}-${item.index}`}>{item.section} 第 {item.index + 1} 筆：{item.reason}</li>)}
                  </ul>
                </section>
              )}

              {jsonPreview.removals.length > 0 && (
                <section className="rounded-md border border-slate-300 bg-slate-50 p-3">
                  <h3 className="text-sm font-semibold">待核實刪除（只供檢視，絕不會自動刪除）</h3>
                  <ul className="mt-2 grid gap-1 text-sm text-slate-700 sm:grid-cols-2">
                    {jsonPreview.removals.map(item => <li key={item.index}>{item.name}</li>)}
                  </ul>
                </section>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeJsonPreview} disabled={importing}>取消</Button>
            <Button onClick={confirmJsonImport} disabled={importing || !jsonImportFile}>
              {importing ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />套用中...</> : "確認並套用變更"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
